import { Link, useNavigate, useParams } from "react-router-dom"
import { useEffect, useState } from "react";
import { abandonCookingProgress, addRecentView, buildShareText, buildPrintHtml, consolidateIngredients, DIETARY_LABEL_KEYS, duplicateRecipe, Ingredient, MethodStep, Recipe, RootState, safeHttpUrl, selectActiveProgressByRecipeId, selectIngredientsByRecipeId, selectMethodStepsByRecipeId, selectRecipeById, selectRecipeLayout, selectRecipeLayoutOverride, setRecipeCooked, setRecipeLayoutOverride, selectRecipeMetaVisibility, selectLocale, setRating, formatDate, useT, useAppDispatch, useAppSelector } from "@reciperoll/core";
import { useShoppingListImport } from "../hooks/useShoppingListImport";
import { Activity, ChevronLeft, Check, Trash2, FolderPlus, Calendar, ShoppingCart, Copy, Share2, Printer, Feather, Link as LinkIcon, AlignLeft, Columns, BookOpen, Slash, Tag, Clock, XCircle } from "react-feather";
import { NotFoundView } from "../components/NotFoundView";
import { RecipeMetaCard } from "../components/RecipeMetaCard";
import { useToast } from "../components/Toast";
import { RecipeIngredientsView } from "../components/RecipeIngredientsView";
import { RecipeImage } from "../components/RecipeImage";
import { RecipeMethodView } from "../components/RecipeMethodView";
import { StartCooking } from "../components/StartCooking";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { OverflowMenu } from "../components/OverflowMenu";
import { AddToCollectionModal } from "../components/AddToCollectionModal";
import { AddToMealPlanModal } from "../components/AddToMealPlanModal";
import { deleteRecipeWithImage } from "../utils/deleteRecipe";
import { useAuth } from "../lib/auth";
import FavouriteButton from "../components/Favourites/FavouriteButton";

export default function RecipeDetail() {
  const t = useT();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [selectedTab, setSelectedTab] = useState<string>("ingredients");
  // Layout is the global preference unless this recipe has its own override.
  const systemLayout = useAppSelector(selectRecipeLayout);
  const layoutOverride = useAppSelector((s: RootState) =>
    selectRecipeLayoutOverride(s, id as string),
  );
  const effectiveLayout = layoutOverride ?? systemLayout;
  const singlePage = effectiveLayout === "single";
  const showMeta = useAppSelector(selectRecipeMetaVisibility);
  const locale = useAppSelector(selectLocale);

  // Get the recipe from the store (undefined for an unknown/deleted id — guarded
  // below once all hooks have run).
  const recipe: Recipe | undefined = useAppSelector((state: RootState) =>
    selectRecipeById(state, id as string),
  );

  // "Added" / "Edited" dates. Hide "Edited" when it lands on the same day as
  // "Added" (a same-day edit) — comparing formatted (day-granularity) strings;
  // both are client-stamped so it's reliable.
  const addedDate = recipe?.createdAt ? formatDate(recipe.createdAt, locale) : null;
  const editedDate = recipe?.contentEditedAt
    ? formatDate(recipe.contentEditedAt, locale)
    : null;
  const showEditedDate = !!editedDate && editedDate !== addedDate;

  const ingredients: Ingredient[] = useAppSelector((state) =>
    selectIngredientsByRecipeId(state, id as string),
  );

  const methodSteps: MethodStep[] = useAppSelector((state) =>
    selectMethodStepsByRecipeId(state, id as string),
  );

  // The active (in-progress) cook, if any — drives the in-progress chip and the
  // "Stop cooking" menu item.
  const activeProgress = useAppSelector((state) =>
    selectActiveProgressByRecipeId(state, id as string),
  );
  const isCooking = !!activeProgress;

  // Servings scaling: the stepper adjusts a target serving count and ingredient
  // quantities scale by target/base. Hidden entirely when the recipe has no
  // servings (nothing to scale from) — scale stays 1.
  const baseServings = recipe?.servings ?? null;
  const [targetServings, setTargetServings] = useState<number>(baseServings ?? 1);
  // Reset when navigating to a different recipe (the route stays mounted) or when
  // the base servings loads/changes.
  useEffect(() => {
    setTargetServings(baseServings ?? 1);
  }, [recipe?.id, baseServings]);
  const scale = baseServings ? targetServings / baseServings : 1;

  const dispatch = useAppDispatch();
  const { session } = useAuth();
  const { importItems, choiceModal } = useShoppingListImport();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addToCollection, setAddToCollection] = useState(false);
  const [addToMealPlan, setAddToMealPlan] = useState(false);
  const toast = useToast();

  // Record the view so it surfaces in the home "Recently Viewed" section.
  useEffect(() => {
    if (id && recipe) dispatch(addRecentView({ recipeId: id }));
  }, [id, recipe, dispatch]);

  // Unknown or deleted id — show the shared not-found state instead of crashing
  // on recipe.* below. Kept after the hooks above so hook order stays stable.
  if (!recipe) {
    return (
      <NotFoundView
        icon={<BookOpen className="w-8 h-8 text-terracotta" />}
        title={t("recipe.notFoundTitle")}
        message={t("recipe.notFoundMessage")}
        actions={
          <Link
            to="/recipes"
            className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 py-2.5 text-sm font-bold text-white"
          >
            {t("recipe.browseRecipes")}
          </Link>
        }
      />
    );
  }

  const handleDelete = () => {
    // Soft-deletes the recipe (syncs) + best-effort removes its Storage image.
    deleteRecipeWithImage(dispatch, recipe.id, session?.user.id, recipe.image);
    navigate("/recipes");
  };

  const handleBack = () => {
    // Return to wherever the recipe was opened from (home, a collection, the
    // library, search…). Fall back to the library only when there's no in-app
    // history to pop — e.g. the recipe was opened via a shared link or a hard
    // refresh landed here directly.
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate("/recipes");
  };

  const addToShoppingList = () => {
    // Never silently merge — importItems asks if any are already on the list.
    importItems(consolidateIngredients(ingredients));
  };

  const handleDuplicate = () => {
    const newId = duplicateRecipe(dispatch, { recipe, ingredients, methodSteps });
    toast.show(t("recipe.duplicated"));
    navigate(`/recipe/${newId}`);
  };

  // Discard the in-progress cook for this recipe. Same wording/behaviour as the
  // home "Continue cooking" card — a browser confirm, then abandon + toast.
  const handleStopCooking = () => {
    if (confirm(t("cook.stopConfirmWeb", { title: recipe.title }))) {
      dispatch(abandonCookingProgress(recipe.id));
      toast.show(t("cook.stoppedCooking", { title: recipe.title }));
    }
  };

  // Clear the recipe's "cooked" flag. Reversible and low-stakes, so no confirm.
  const handleMarkNotCooked = () => {
    dispatch(setRecipeCooked({ id: recipe.id, cooked: false }));
    toast.show(t("recipe.markedNotCooked", { title: recipe.title || t("recipe.thisRecipe") }));
  };

  // Per-recipe override of the global tabs/single-page layout. Toggling back to
  // the global value clears the override (passes null) rather than pinning it.
  const toggleLayout = () => {
    const target = effectiveLayout === "tabs" ? "single" : "tabs";
    dispatch(
      setRecipeLayoutOverride({
        recipeId: recipe.id,
        layout: target === systemLayout ? null : target,
      }),
    );
  };

  // Copy the full source URL (with scheme) — the visible link is truncated and
  // scheme-stripped, so this one-tap copy gives the whole URL.
  const copySourceUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.show(t("recipe.linkCopied"));
    } catch {
      toast.show(t("recipe.copyFailed"));
    }
  };

  const handleShare = async () => {
    const text = buildShareText(recipe, ingredients, methodSteps);
    // Native share sheet where available (mobile / some desktop); otherwise copy
    // the recipe text to the clipboard.
    if (navigator.share) {
      try {
        await navigator.share({ title: recipe.title, text });
      } catch {
        // user dismissed the share sheet — nothing to do
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.show(t("recipe.copiedToClipboard"));
    } catch {
      toast.show(t("recipe.copyFailed"));
    }
  };

  const handlePrint = () => {
    // Render the shared print HTML into a hidden iframe and print just that —
    // avoids popup blockers and prints the recipe, not the whole app.
    const html = buildPrintHtml(recipe, ingredients, methodSteps);
    const iframe = document.createElement("iframe");
    Object.assign(iframe.style, {
      position: "fixed",
      right: "0",
      bottom: "0",
      width: "0",
      height: "0",
      border: "0",
    });
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    const doc = win?.document;
    if (!win || !doc) {
      iframe.remove();
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      win.focus();
      win.print();
      setTimeout(() => iframe.remove(), 1000);
    };
    iframe.onload = () => setTimeout(doPrint, 200);
    setTimeout(doPrint, 600); // fallback if onload doesn't fire
  };

  return (
    <div data-view="detail" className="min-h-screen bg-surface active">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <button onClick={handleBack} className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink-soft hover:text-ink mb-6">
          <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
          {t("common.back")}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-10">
          {/* Image + sticky cook bar */}
          <div>
            {/* Hero: the recipe image, or a placeholder glyph when there's none
                (keeps the two-column layout balanced instead of a blank box). */}
            <div className="rounded-3xl overflow-hidden h-[320px] mb-5 bg-terracotta-wash">
              <RecipeImage recipe={recipe} />
            </div>
            <StartCooking
              recipeId={id as string}
              baseServings={baseServings}
              initialServings={targetServings}
            />
            <div className="flex gap-2">
              <Link to={`/recipe/${id}/edit`} className="flex-1 bg-surface-2 hover:bg-line transition-colors rounded-xl py-3 text-sm font-semibold text-ink-soft text-center">{t("recipe.editRecipe")}</Link>
              <FavouriteButton recipe={recipe} className="w-12 h-12 shrink-0 bg-surface-2 hover:bg-line transition-colors rounded-xl flex items-center justify-center" />
              <OverflowMenu
                buttonClassName="w-12 h-12 bg-surface-2 hover:bg-line transition-colors rounded-xl flex items-center justify-center text-ink-soft cursor-pointer"
                items={[
                  {
                    label: t("recipe.addToCollection"),
                    icon: <FolderPlus className="w-[18px] h-[18px]" />,
                    onClick: () => setAddToCollection(true),
                  },
                  {
                    label: t("recipe.addToMealPlan"),
                    icon: <Calendar className="w-[18px] h-[18px]" />,
                    onClick: () => setAddToMealPlan(true),
                  },
                  {
                    label: t("recipe.addToShoppingList"),
                    icon: <ShoppingCart className="w-[18px] h-[18px]" />,
                    onClick: addToShoppingList,
                  },
                  {
                    label: t("recipe.duplicate"),
                    icon: <Copy className="w-[18px] h-[18px]" />,
                    onClick: handleDuplicate,
                  },
                  // Shown only mid-cook: discard the active cooking session.
                  ...(isCooking
                    ? [
                        {
                          label: t("recipe.stopCooking"),
                          icon: <Slash className="w-[18px] h-[18px]" />,
                          onClick: handleStopCooking,
                        },
                      ]
                    : []),
                  // Shown only when the recipe is currently marked cooked.
                  ...(recipe.cooked
                    ? [
                        {
                          label: t("recipe.markNotCooked"),
                          icon: <XCircle className="w-[18px] h-[18px]" />,
                          onClick: handleMarkNotCooked,
                        },
                      ]
                    : []),
                  {
                    label: t("recipe.cookingHistory"),
                    icon: <Clock className="w-[18px] h-[18px]" />,
                    onClick: () => navigate(`/recipe/${id}/history`),
                  },
                  {
                    label: effectiveLayout === "tabs" ? t("recipe.viewAsSinglePage") : t("recipe.viewAsTabs"),
                    icon:
                      effectiveLayout === "tabs" ? (
                        <AlignLeft className="w-[18px] h-[18px]" />
                      ) : (
                        <Columns className="w-[18px] h-[18px]" />
                      ),
                    onClick: toggleLayout,
                  },
                  {
                    label: t("recipe.share"),
                    icon: <Share2 className="w-[18px] h-[18px]" />,
                    onClick: handleShare,
                  },
                  {
                    label: t("recipe.print"),
                    icon: <Printer className="w-[18px] h-[18px]" />,
                    onClick: handlePrint,
                  },
                  {
                    label: t("recipe.deleteRecipe"),
                    icon: <Trash2 className="w-[18px] h-[18px]" />,
                    destructive: true,
                    onClick: () => setConfirmDelete(true),
                  },
                ]}
              />
            </div>
          </div>

          {/* Content — min-w-0 lets this 1fr grid track shrink below its content's
              intrinsic width, so long titles wrap and the source/URL can truncate
              instead of forcing the whole column (and page) to overflow. */}
          <div className="min-w-0">
            <h1 className="font-display text-4xl font-extrabold text-ink leading-tight mb-4 break-words">{recipe.title}</h1>

            {(recipe.source || recipe.sourceUrl) && (
              <div className="flex flex-col gap-1.5 mb-6">
                {recipe.source && (
                  <div className="flex items-start gap-2 text-sm font-semibold text-ink-soft">
                    <Feather className="w-3.5 h-3.5 shrink-0 mt-0.5 text-terracotta" />
                    <span className="min-w-0 break-words">{recipe.source}</span>
                  </div>
                )}
                {(() => {
                  // React doesn't sanitize href, so only bind an http(s) URL —
                  // never a javascript:/data: scheme (sourceUrl is free-text +
                  // import + synced). See safeHttpUrl.
                  const safeSourceUrl = safeHttpUrl(recipe.sourceUrl);
                  if (!safeSourceUrl) return null;
                  return (
                    <div className="flex items-center gap-2 w-fit max-w-full">
                      <a
                        href={safeSourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm font-semibold text-terracotta hover:text-terracotta-dark min-w-0"
                      >
                        <LinkIcon className="w-3.5 h-3.5 shrink-0" />
                        <span className="truncate">
                          {safeSourceUrl.replace(/^https?:\/\//i, "")}
                        </span>
                      </a>
                      <button
                        type="button"
                        onClick={() => copySourceUrl(safeSourceUrl)}
                        aria-label={t("recipe.copyLink")}
                        title={t("recipe.copyLink")}
                        className="shrink-0 p-1 text-terracotta hover:text-terracotta-dark cursor-pointer"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* In-progress indicator — shown while a cook is active for this
                recipe, mirroring the RN detail chip. */}
            {isCooking && (
              <div className="mb-6">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta-wash px-3 py-1.5 text-xs font-semibold text-terracotta-ink">
                  <Activity className="w-3.5 h-3.5" />
                  {methodSteps.length
                    ? t("recipe.cookingWithStep", {
                        step: activeProgress?.currentStep,
                        total: methodSteps.length,
                      })
                    : t("recipe.cooking")}
                </span>
              </div>
            )}

            <RecipeMetaCard
              recipe={recipe}
              showMeta={showMeta}
              onRate={(r) => dispatch(setRating({ id: recipe.id, rating: r }))}
            />

            {/* Dietary suitability — green check pills, same label keys as RN. */}
            {!!recipe.dietary?.length && (
              <div className="flex flex-wrap gap-2 mb-6 mt-6">
                {recipe.dietary.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-900/40 px-3 py-1.5 text-xs font-semibold text-green-700 dark:text-green-300"
                  >
                    <Check className="w-3 h-3" strokeWidth={3} />
                    {DIETARY_LABEL_KEYS[tag] ? t(DIETARY_LABEL_KEYS[tag]) : tag}
                  </span>
                ))}
              </div>
            )}

            {recipe.description && (
              <p className="text-[15px] text-ink-soft leading-relaxed mb-8 max-w-xl whitespace-pre-wrap">
                {recipe.description}
              </p>
            )}

            {showMeta.tags && !!recipe.tags?.length && (
              <div className="flex flex-wrap gap-2 mb-6">
                {recipe.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 bg-surface-2 rounded-full px-3 py-1.5 text-xs font-semibold text-ink-soft"
                  >
                    <Tag className="w-3 h-3 text-terracotta" />
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Tab bar — hidden in single-page mode. */}
            {!singlePage && (
              <div className="flex border-b border-line mb-6 gap-6 overflow-x-auto no-scrollbar">
                {!!ingredients.length && <button onClick={() => setSelectedTab("ingredients")} className={`pb-3 text-sm font-bold border-b-2 ${selectedTab === "ingredients" ? " border-terracotta text-terracotta" : "cursor-pointer border-transparent text-ink-faint hover:text-ink-soft"}`}>{t("recipe.ingredients")}</button>}
                {!!methodSteps.length && <button onClick={() => setSelectedTab("method")} className={`pb-3 text-sm font-semibold border-b-2 ${selectedTab === "method" ? " border-terracotta text-terracotta" : "cursor-pointer border-transparent text-ink-faint hover:text-ink-soft"}`}>{t("recipe.method")}</button>}
                {!!recipe.notes && <button onClick={() => setSelectedTab("notes")} className={`pb-3 text-sm font-semibold ${selectedTab === "notes" ? " border-terracotta text-terracotta" : "cursor-pointer border-transparent text-ink-faint hover:text-ink-soft"}`}>{t("recipe.notes")}</button>}
              </div>
            )}

            {/* Ingredients */}
            {!!ingredients.length && (singlePage || selectedTab === "ingredients") && (
              <section className={singlePage ? "mb-8" : ""}>
                {singlePage && (
                  <h2 className="font-display text-xl font-semibold text-ink mb-4">{t("recipe.ingredients")}</h2>
                )}
                {baseServings != null && (
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-xs font-bold uppercase tracking-wide text-ink-faint">{t("recipe.servings")}</span>
                    <div className="flex items-center bg-surface-2 rounded-lg">
                      <button
                        onClick={() => setTargetServings((s) => Math.max(1, s - 1))}
                        disabled={targetServings <= 1}
                        className="w-8 h-8 flex items-center justify-center text-ink-soft hover:text-ink font-bold disabled:opacity-40 disabled:hover:text-ink-soft"
                      >–</button>
                      <span className="w-8 text-center text-sm font-bold text-ink">{targetServings}</span>
                      <button
                        onClick={() => setTargetServings((s) => s + 1)}
                        className="w-8 h-8 flex items-center justify-center text-ink-soft hover:text-ink font-bold"
                      >+</button>
                    </div>
                  </div>
                )}
                <RecipeIngredientsView ingredients={ingredients} scale={scale} />
              </section>
            )}

            {/* Method */}
            {!!methodSteps.length && (singlePage || selectedTab === "method") && (
              <section className={singlePage ? "mb-8" : ""}>
                {singlePage && (
                  <h2 className="font-display text-xl font-semibold text-ink mb-4">{t("recipe.method")}</h2>
                )}
                <RecipeMethodView steps={methodSteps} />
              </section>
            )}

            {/* Notes */}
            {!!recipe.notes && (singlePage || selectedTab === "notes") && (
              <section>
                {singlePage && (
                  <h2 className="font-display text-xl font-semibold text-ink mb-4">{t("recipe.notes")}</h2>
                )}
                <p className="text-[15px] text-ink leading-relaxed whitespace-pre-wrap max-w-xl">
                  {recipe.notes}
                </p>
              </section>
            )}

            {/* When the recipe was added / last content-edited — muted, at the
                bottom. "Edited" only shows once edited and on a later day than added. */}
            {(!!addedDate || showEditedDate) && (
              <div className="pt-2 space-y-0.5">
                {!!addedDate && (
                  <p className="text-xs text-ink-faint">
                    {t("recipe.addedOn", { date: addedDate })}
                  </p>
                )}
                {showEditedDate && (
                  <p className="text-xs text-ink-faint">
                    {t("recipe.editedOn", { date: editedDate })}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {confirmDelete && (
        <ConfirmDialog
          title={t("recipe.deleteConfirmTitle")}
          message={t("recipe.deleteConfirmMessage")}
          confirmLabel={t("common.delete")}
          confirmClass="bg-red-500 hover:bg-red-600"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      {addToCollection && (
        <AddToCollectionModal recipeId={recipe.id} onClose={() => setAddToCollection(false)} />
      )}
      {addToMealPlan && (
        <AddToMealPlanModal
          recipeId={recipe.id}
          recipeTitle={recipe.title}
          onClose={() => setAddToMealPlan(false)}
        />
      )}
      {choiceModal}
    </div>

  )
}